import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Instructions from './Instructions'
import MissionCritical from './MissionCritical'
import Sleep from './Sleep'
import CaffeineType from './CaffeineType'
import CaffeineTime from './CaffeineTime'
import OnboardingComplete from './OnboardingComplete'
import {
  OnboardingProvider,
  useOnboarding,
} from '../../contexts/OnboardingContext'
import useUserPreferencesFetcher from '../../hooks/useUserPreferencesFetcher'
import { RequestService } from '../../services/RequestService'
import { useSnackbar } from '../../hooks'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  justify-content: center;
  padding: 44px;
`

const OnboardingSteps = () => {
  const { errorSnackbar } = useSnackbar()
  const [currentStep, setCurrentStep] = useState(0)
  const {
    setAvailableShift,
    setSleepTime,
    setAwakeTime,
    setCaffeineProducts,
    setSelectedCaffeineOptions,
    setCaffeineTime,
    selectedCaffeineOptions,
    caffeineTime,
    sleepTime,
    awakeTime,
    workJourneyValues,
  } = useOnboarding()
  const { loading, data } = useUserPreferencesFetcher()
  const navigate = useNavigate()

  useEffect(() => {
    if (data) {
      const {
        shift,
        sleepTime,
        awakeTime,
        caffeineProducts,
        caffeinePreferences,
        caffeineTime,
      } = data

      setAvailableShift(shift)
      if (sleepTime) setSleepTime(sleepTime)
      if (awakeTime) setAwakeTime(awakeTime)
      setCaffeineProducts(caffeineProducts)
      setSelectedCaffeineOptions(caffeinePreferences)
      if (caffeineTime) setCaffeineTime(caffeineTime)
    }
  }, [data])

  const handleNext = () => {
    setCurrentStep(prevStep => prevStep + 1)
  }

  const handlePrevious = () => {
    setCurrentStep(prevStep => prevStep - 1)
  }

  const handleFinishedOnboarding = async () => {
    const selectedShift = workJourneyValues !== 'shiftIncorrect' ? true : false

    const selectedCaffeineOptionsIds = selectedCaffeineOptions.map(
      option => option.id,
    )

    const selectedCaffeineTime = selectedCaffeineOptions[0]?.isNoCaffeine
      ? null
      : caffeineTime

    const response = await RequestService.saveUserOnboardingPreferences({
      shift: selectedShift,
      sleepTime,
      awakeTime,
      caffeinePreferences: selectedCaffeineOptionsIds,
      caffeineTime: selectedCaffeineTime,
    })

    if (response.status === RequestService.STATUSES.SUCCESS) {
      navigate('/intake')
    } else {
      errorSnackbar({ text: response.message })
    }
  }

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <Instructions
            onNext={handleNext}
            loading={loading}
          />
        )
      case 1:
        return (
          <MissionCritical
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        )
      case 2:
        return (
          <Sleep
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        )
      case 3:
        return (
          <CaffeineType
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        )
      case 4:
        return (
          <CaffeineTime
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        )
      case 5:
        return (
          <OnboardingComplete
            onNext={handleFinishedOnboarding}
            onPrevious={handlePrevious}
          />
        )
      default:
        return null
    }
  }

  return <Container>{renderStep()}</Container>
}

const Onboarding = () => {
  return (
    <OnboardingProvider>
      <OnboardingSteps />
    </OnboardingProvider>
  )
}

export default Onboarding
